import { format } from "date-fns";
import Button from "devextreme-react/button";
import Form, { GroupItem, Item, RequiredRule } from "devextreme-react/form";
import HtmlEditor from "devextreme-react/html-editor";
import LoadPanel from "devextreme-react/load-panel";
import SelectBox from "devextreme-react/select-box";
import Toolbar from "devextreme-react/toolbar";
import "devextreme/ui/html_editor";
import React, { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { CommentType } from "../../../../../dtos/CommentType";
import { GrantDto } from "../../../../../dtos/GrantDto";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { PhaseWorkflowType } from "../../../../../dtos/PhaseWorkflowType";
import { StepDto } from "../../../../../dtos/StepDto";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { StepWorkflowType } from "../../../../../dtos/StepWorkflowType";
import { TrackedMode } from "../../../../../dtos/TrackedMode";
import { UserRole } from "../../../../../dtos/UserRole";
import { getStepStatusInfo } from "../../../Utils";
import SubTaskComments from "../../subtasks/SubTaskComments";
import SubTaskRevisionComments from "../../subtasks/SubTaskRevisionComments";
import SubTaskStatusButtons from "../../subtasks/SubTaskStatusButtons";
import TaskFileUploader from "../../subtasks/components/FileUploader";
import SubmissionFileUploaders from "../../subtasks/components/SubmissionFileUploaders";
import { configuredDocuments } from "../../stepFiles";
import TaskFiles from "../../subtasks/components/Files";
import "./TasksView.css";
import { PhaseInfoItem, TasksFeatureFlags } from "../../../Types";
import { useAppContext } from "../../../AppContext";
import { TasklistMode } from "../../context/TaskListContext";
import SubTaskList from "../../subtasks/SubTaskList";
import TaskSummary from "../TaskSummary";
import { TaskCycleIndicator } from "./TaskCycleIndicator";
import { cycleVocabulary } from "../taskCycle";
import useFillViewportHeight from "../../../../../hooks/useFillViewportHeight";
import LinkifiedText, { LinkifiedHtml } from "../../../LinkifiedText";

// A standard window that fits about five subtasks - a typical DC phase - before the list scrolls.
const TASKS_MIN_HEIGHT = 260;
// The tallest the box gets. The list scrolls past this rather than growing, so a task with
// several submissions' worth of subtasks reads the same as a task with a handful.
const TASKS_MAX_HEIGHT = 475;

export type ITasksViewProps = {
    registerLoadPanel: (showFn: () => void, hideFn: () => void) => void;

    xsrf: { value: string | null | undefined };
    showSummary: boolean;
    selectedSummaryPhase: PhaseDto | null;
    initialLoad: boolean;

    readyDisabled: React.MutableRefObject<boolean>;
    completeDisabled: React.MutableRefObject<boolean>;

    htmlEditorExpanded: boolean;
    htmlEditorText: React.MutableRefObject<string | null>;
    htmlEditorTarget: React.MutableRefObject<"request" | "response" | null>;

    setHtmlEditorExpanded: (v: boolean) => void;
    onClickSave: (skipRefresh?: boolean) => Promise<void>;
    onClickComplete: () => Promise<void>;
    onClickClose: () => void;
    onFieldDataChanged: (e: { dataField?: string; value?: any }) => void;

    nameDescriptionDisabled: () => boolean;
    dueDateDisabled: () => boolean;
    trackingDatesDisabled: () => boolean;
    projectedStartDateOptions: { displayFormat: string; readOnly: boolean; min?: Date; max?: Date };
    projectedCompletionDateOptions: { displayFormat: string; readOnly: boolean; min?: Date; max?: Date };
    startDateOptions: { displayFormat: string; readOnly: boolean; min?: Date; max?: Date };
    uploadDisabled: () => boolean;
    requestUploadDisabled: () => boolean;
    responseUploadDisabled: () => boolean;
    requestEditDisabled: () => boolean;
    requestIsVisible: () => boolean;
    responseEditDisabled: () => boolean;
    responseIsVisible: () => boolean;
    commentsAreVisible: () => boolean;
    canEditAnything: boolean;
    isCycleClosed: boolean;

    nameOptions: any;
    dateOptions: any;
    dateFieldName: string;
    dateFieldLabel: string;

    handleHtmlRequestChange: (val: string) => void;
    handleHtmlResponseChange: (val: string) => void;
    handleDescriptionChange: (val: string) => void;

    headerDueDateText: () => string | null;
    requestCommentDate: () => string;
    responseCommentDate: () => string;

    handleDeleteClick: () => Promise<void>;
    deleteButtonDisabled: () => boolean;

    handleTaskLoading: () => void;
    handleTaskLoaded: () => void;
    handleTaskVisibilityChange: (index: number, visible: boolean) => void;
    handleTaskCollapsedChange: (index: number, collapsed: boolean) => void;
    // Every task in this box is collapsed, so there is nothing below the toolbars to hold open.
    everyTaskCollapsed: boolean;
    hasAnyVisibleTasks: boolean;

    showPhaseSummary: (p: PhaseDto | null) => void;
    hidePhaseSummary: () => void;
    showPhaseRequestResponseSummary: (p: PhaseDto | null) => void;
    hidePhaseRequestResponseSummary: () => void;

    // passthrough
    grantData: GrantDto;
    selectedStep: StepDto | null;
    selectedPhase: PhaseDto | null;
    phasesInfo: PhaseInfoItem[];
    deepLinkHash: string | null;
    nameShownExternally?: boolean;
    // The page's own copy of the phase, passed through to each tasklist - see ITasksProps.
    externalPhase?: PhaseDto | null;

    getStepName: () => string | null;

    mode: TasklistMode;
    flags: TasksFeatureFlags;
    workflowType: PhaseWorkflowType | null | undefined;
    // The task's subtasks are document folders: files only.
    uploadOnly: boolean;

    // Ad-hoc subtask workflow type picker (only shown for new subtasks under hybrid parent)
    isAdhocHybridNew: boolean;
    subtaskWorkflowOptions: { id: StepWorkflowType; name: string }[];
    adhocSubtaskWorkflowType: StepWorkflowType | null;
    setAdhocSubtaskWorkflowType: (value: StepWorkflowType | null) => void;
    refreshSteps: (selectedStep?: StepDto) => Promise<void>;
    selectedSubmissionId: string | null;
};

// Labels for the form's date and description items. Module-level so they are the same object on
// every render: a nested item option that changes identity makes the DX Form rebuild itself (see
// the editor options below).
const PROJECTED_START_LABEL = { text: "Projected Start" };
const PROJECTED_COMPLETION_LABEL = { text: "Projected Completion" };
const START_DATE_LABEL = { text: "Start Date" };
const DESCRIPTION_LABEL = { text: "Description" };
// The read-only description carries its title in its own frame.
const DESCRIPTION_DISPLAY_LABEL = { text: "Description", visible: false };
// Instructions routinely carry a bare URL, which is one unbreakable word: without the wrap it ran
// straight out of its column and under the file list.
const INSTRUCTIONS_STYLE: React.CSSProperties = {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    fontSize: "13px",
    padding: "4px 0",
};

// Options for the request/response HtmlEditor: a title in the toolbar, and the formatting buttons
// plus the expand button only while the editor is writable.
function htmlEditorOptions(
    title: string,
    titleClass: string,
    readOnly: boolean,
    height: number,
    onChange: (value: string) => void,
    onExpand: () => void,
) {
    const titleItem = { text: title, location: "before", cssClass: titleClass };
    return {
        readOnly,
        height: readOnly ? "auto" : height,
        onValueChanged: (e: any) => onChange(e.value),
        toolbar: readOnly
            ? { items: [titleItem] }
            : {
                items: [
                    titleItem,
                    "bold", "italic", "strike", "underline", "separator",
                    "alignLeft", "alignCenter", "alignRight", "alignJustify", "separator",
                    "orderedList", "bulletList", "separator",
                    {
                        widget: "dxButton",
                        options: { icon: "fullscreen", height: "60%", hint: "Expand Editor", onClick: onExpand },
                        location: "after",
                    },
                ],
            },
    };
}

// Read-only description, request and response are shown as text rather than locked editors, so
// their links can be clicked. Each is drawn in the read-only rich text editor's frame (the same DX
// classes): dashed border and the toolbar title. The content class keeps the editor's list and
// indent styling, which is scoped to it.
function readOnlyEditorDisplay(title: string, titleClass: string, testId: string, content: React.ReactNode) {
    return (
        <div className="dx-widget dx-htmleditor dx-htmleditor-outlined dx-state-readonly" data-testid={testId}>
            <div className="dx-htmleditor-toolbar-wrapper">
                <Toolbar className="dx-htmleditor-toolbar" items={[{ text: title, location: "before", cssClass: titleClass }]} />
            </div>
            {content}
        </div>
    );
}

export function TasksView(props: ITasksViewProps) {
    const {
        registerLoadPanel,

        xsrf,
        showSummary,
        selectedSummaryPhase,
        initialLoad,

        readyDisabled,
        completeDisabled,

        htmlEditorExpanded,
        htmlEditorText,
        htmlEditorTarget,

        setHtmlEditorExpanded,
        onClickSave,
        onClickComplete,
        onClickClose,
        onFieldDataChanged,

        nameDescriptionDisabled,
        dueDateDisabled,
        trackingDatesDisabled,
        projectedStartDateOptions,
        projectedCompletionDateOptions,
        startDateOptions,
        uploadDisabled,
        requestUploadDisabled,
        responseUploadDisabled,
        requestEditDisabled,
        requestIsVisible,
        responseEditDisabled,
        responseIsVisible,
        commentsAreVisible,
        canEditAnything,
        isCycleClosed,

        nameOptions,
        dateOptions,
        dateFieldName,
        dateFieldLabel,

        handleHtmlRequestChange,
        handleHtmlResponseChange,
        handleDescriptionChange,

        headerDueDateText,
        requestCommentDate,
        responseCommentDate,

        handleDeleteClick,
        deleteButtonDisabled,

        handleTaskLoading,
        handleTaskLoaded,
        handleTaskVisibilityChange,
        hasAnyVisibleTasks,
        handleTaskCollapsedChange,
        everyTaskCollapsed,

        showPhaseSummary,
        hidePhaseSummary,
        showPhaseRequestResponseSummary,
        hidePhaseRequestResponseSummary,

        grantData,
        selectedStep,
        selectedPhase,
        phasesInfo,
        deepLinkHash,
        nameShownExternally,
        externalPhase,

        getStepName,

        mode,
        flags,
        workflowType,
        uploadOnly,
        isAdhocHybridNew,
        subtaskWorkflowOptions,
        adhocSubtaskWorkflowType,
        setAdhocSubtaskWorkflowType,
        refreshSteps,
        selectedSubmissionId,
    } = props;

    const app = useAppContext();

    // Every task on the page renders one of these, so the container id has to be per-instance:
    // as a shared id the LoadPanel positioned itself over whichever task came first in the DOM,
    // which is why opening CD put its spinner over DD (ticket 1034). Styling hangs off the
    // matching class instead.
    const reactId = useId();
    const containerId = `task-main-requests-${reactId.replace(/[:]/g, "")}`;

    // The box grows to the bottom of the window instead of stopping at a fixed cap. DC opens each
    // phase in its own accordion panel and more than one can be open at once, so only fill the
    // window when this box has the space below it to itself; stacked panels keep the old cap and
    // share the page.
    const canFillViewport = useCallback(() => {
        // A TabPanel keeps the tabs you are not looking at mounted and parks them off-screen, so
        // counting every box on the page would see Pre-award's while you are on Design/Construction
        // and stop both from filling. Only boxes in the open tab count.
        const boxes = Array.from(document.getElementsByClassName("task-main-requests"));
        const onScreen = boxes.filter(b => !b.closest('.dx-multiview-item-hidden, [aria-hidden="true"]'));
        return onScreen.length === 1;
    }, []);

    // In templates mode the shell around this box already sizes itself to the window, so measuring
    // the window again here would give two answers for the same space - and on a short window the
    // fallback cap is taller than the shell, which left the list hanging out below it.
    const fillsParent = flags.hideDetailView === true;
    const { maxHeight: roomBelow, ref: containerRef } = useFillViewportHeight<HTMLDivElement>({
        // Clears the 8px bottom margin on .grantDetailsPanelTop plus a little breathing room.
        gutter: 16,
        enabled: !fillsParent,
        canFill: canFillViewport,
    });

    // The window is the other ceiling, and only when there is a usable amount of room below: in DC
    // the box can sit under eight rows of phase dates, low enough that the space left is under the
    // minimum a list needs. Whichever ceiling is lower wins, so the box never runs past the bottom
    // of the window and never grows past the cap either.
    const fillsViewport = roomBelow !== undefined && roomBelow >= TASKS_MIN_HEIGHT;
    const maxHeight = fillsViewport ? Math.min(roomBelow!, TASKS_MAX_HEIGHT) : TASKS_MAX_HEIGHT;

    // LoadPanel ref + wire-up to controller
    const taskListloadPanel = useRef<LoadPanel>(null);
    // Show the scoped panel only when the global one isn't already up, so the two don't stack.
    const showTaskListPanel = () => {
        if (app.isLoadPanelVisible?.()) return;
        taskListloadPanel.current?.instance?.show();
    };
    useEffect(() => {
        const show = () => taskListloadPanel.current?.instance?.show();
        const hide = () => taskListloadPanel.current?.instance?.hide();
        registerLoadPanel(show, hide);
    }, [registerLoadPanel]);

    // Rendered helpers (format ISO strings coming from controller if used)
    const requestUpdatedDisplay = useMemo(() => {
        const s = requestCommentDate();
        if (!s) return "";
        // Controller gave either ISO or empty string; format here if needed
        const d = new Date(s);
        return isNaN(d.getTime()) ? "" : format(d, "MM/dd/yyyy h:mm a");
    }, [requestCommentDate]);

    const responseUpdatedDisplay = useMemo(() => {
        const s = responseCommentDate();
        if (!s) return "";
        const d = new Date(s);
        return isNaN(d.getTime()) ? "" : format(d, "MM/dd/yyyy h:mm a");
    }, [responseCommentDate]);

    // callback for onAdding to prevent SubTaskList re-renders
    const handleCollapseHtmlEditor = useCallback(() => {
        setHtmlEditorExpanded(false);
    }, [setHtmlEditorExpanded]);

    // The request and response editors' options are built here rather than inline in the markup.
    // Inline, the object literals were new on every render, and a nested item option that changes
    // makes the DX Form rebuild itself - which remounts everything in it, including a file upload in
    // progress and the document upload dialog. The view re-renders on every window resize (the
    // viewport-height measurement), so opening dev tools was enough to lose an upload.
    const requestEditorReadOnly = requestEditDisabled();
    const responseEditorReadOnly = responseEditDisabled();
    const dueDateLabel = useMemo(() => ({ text: dateFieldLabel }), [dateFieldLabel]);
    const descriptionReadOnly = nameDescriptionDisabled();
    const descriptionOptions = useMemo(() => ({
        readOnly: descriptionReadOnly,
        height: 200,
        maxLength: 2000,
        valueChangeEvent: "input",
        onValueChanged: (e: any) => handleDescriptionChange(e.value),
    }), [descriptionReadOnly, handleDescriptionChange]);
    const requestLabel = useMemo(() => ({ text: "Request", visible: false }), []);
    const responseLabel = useMemo(() => ({ text: "Response", visible: false }), []);
    const requestEditorOptions = useMemo(
        () => htmlEditorOptions("Request", "requestTitle", requestEditorReadOnly, 280, handleHtmlRequestChange, () => {
            htmlEditorTarget.current = "request";
            setHtmlEditorExpanded(true);
        }),
        [requestEditorReadOnly, handleHtmlRequestChange, htmlEditorTarget, setHtmlEditorExpanded]
    );
    const responseEditorOptions = useMemo(
        () => htmlEditorOptions("Response", "responseTitle", responseEditorReadOnly, 300, handleHtmlResponseChange, () => {
            htmlEditorTarget.current = "response";
            setHtmlEditorExpanded(true);
        }),
        [responseEditorReadOnly, handleHtmlResponseChange, htmlEditorTarget, setHtmlEditorExpanded]
    );

    // If hideDetailView is true, only show the task list (for templates with external tab editor)
    const hideDetailView = flags.hideDetailView === true;

    // Upload-only subtasks show instructions where other workflow types show the description, which
    // puts the file list beside them instead of across the full width. With no instructions there
    // is nothing to sit next to, so the file list keeps the full width.
    // Collapsed: the box has nothing below the toolbars to hold it open, so it closes up to them.
    // An open subtask or summary closes with it rather than keeping the box open - collapsing a task
    // with the extended header hides its whole panel the same way, and the two must read alike. The
    // selection is left alone, so expanding again brings back whatever was open.
    const boxIsEmpty = everyTaskCollapsed;

    const submissionSplitLayout = uploadOnly && !!selectedStep?.defaultStep?.instructions;

    // Upload-only subtasks in a grant upload through named slots or a catch-all picker; the
    // templates admin edits the step's own default files with the generic uploader instead. A
    // subtask with configured documents uploads through its document rows whatever task it sits
    // under, so the free-form picker above the list has to give way there too - otherwise the same
    // subtask offers two ways to add a file and only one of them routes to a document.
    const hasConfiguredDocuments = !!configuredDocuments(selectedStep?.defaultStep?.settings);
    const submissionUploads = mode !== "templates" && (uploadOnly || hasConfiguredDocuments);

    // Locked name and due date are hidden with visible, not left out of the tree. DX updates a
    // Form in place when the group count is unchanged, and a group whose items all disappear
    // between renders has its items reset to undefined - which the group then fills with an
    // editor for every field on the step.
    const showName = !nameDescriptionDisabled();
    const showDueDate = !dueDateDisabled() && !uploadOnly;

    // The read-only displays, memoized for the same reason as the editor options above.
    const description = selectedStep?.description;
    const descriptionRender = useCallback(
        () => readOnlyEditorDisplay("Description", "descriptionTitle", "description-display",
            <div className="dx-htmleditor-content"><LinkifiedText text={description} /></div>),
        [description]
    );

    const requestHtml = selectedStep?.requestComment?.comment;
    const responseHtml = selectedStep?.responseComment?.comment;
    const requestRender = useCallback(
        () => readOnlyEditorDisplay("Request", "requestTitle", "request-display",
            <LinkifiedHtml html={requestHtml} className="dx-htmleditor-content" />),
        [requestHtml]
    );
    const responseRender = useCallback(
        () => readOnlyEditorDisplay("Response", "responseTitle", "response-display",
            <LinkifiedHtml html={responseHtml} className="dx-htmleditor-content" />),
        [responseHtml]
    );

    return (
        // Height follows content between a min and a max (ticket 1015): the min gives the subtask
        // list a standard window that fits about five subtasks (a typical DC phase) before the list
        // scrolls, and tall detail forms scroll inside the form pane rather than growing the whole
        // box past the max. The max is measured against the window (ticket 1046) so the box never
        // runs past the bottom of the viewport and follows a resize; the fixed value is the
        // fallback for the stacked-panel case. It stays a max rather than a set height: a set
        // height made a task with a handful of subtasks and nothing selected into a window-tall
        // box of empty space, which is what DC's Schematic Design shows as the only phase on the
        // page rendering a tasklist. position relative anchors the expanded-editor overlay and the
        // LoadPanel, which used to resolve against an ancestor.
        <div
            id={containerId}
            ref={containerRef}
            className="task-main-requests"
            style={{
                display: "flex",
                position: "relative",
                minHeight: fillsParent || boxIsEmpty ? 0 : TASKS_MIN_HEIGHT,
                ...(fillsParent
                    ? { height: "100%" }
                    : { maxHeight }),
            }}
        >
            {selectedStep && htmlEditorExpanded && !hideDetailView && (
                <div className="expanded-editor-overlay">
                    <HtmlEditor
                        defaultValue={htmlEditorText.current ?? ""}
                        onValueChanged={(e) => (htmlEditorText.current = e.value)}
                        height="100%"
                        toolbar={{
                            items: [
                                {
                                    text:
                                        htmlEditorTarget.current === "request"
                                            ? "Request"
                                            : htmlEditorTarget.current === "response"
                                                ? "Response"
                                                : "",
                                    location: "before",
                                    cssClass:
                                        htmlEditorTarget.current === "request"
                                            ? "requestTitle"
                                            : htmlEditorTarget.current === "response"
                                                ? "responseTitle"
                                                : "",
                                },
                                "undo",
                                "redo",
                                "separator",
                                "bold",
                                "italic",
                                "strike",
                                "underline",
                                "separator",
                                "alignLeft",
                                "alignCenter",
                                "alignRight",
                                "alignJustify",
                                "separator",
                                "orderedList",
                                "bulletList",
                                "separator",
                                {
                                    widget: "dxButton",
                                    location: "after",
                                    options: {
                                        icon: "collapse",
                                        hint: "Collapse Editor",
                                        onClick: () => {
                                            if (htmlEditorTarget.current === "request") {
                                                if (selectedStep) {
                                                    selectedStep.requestComment.comment = htmlEditorText.current;
                                                }
                                            } else if (htmlEditorTarget.current === "response") {
                                                if (selectedStep) {
                                                    selectedStep.responseComment.comment = htmlEditorText.current;
                                                }
                                            }
                                            setHtmlEditorExpanded(false);
                                            htmlEditorTarget.current = null;
                                        },
                                    },
                                },
                            ],
                        }}
                    />
                </div>
            )}

            {/* Under an external phase header (DC) the subtask column ends where the header's title
                column does, so it - and its scrollbar - stop short of the NoA column above. */}
            <div style={{ display: "flex", padding: "0px", width: hideDetailView ? "100%" : nameShownExternally ? "21%" : "25%", flexDirection: "column", minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
                {phasesInfo.map((item, idx) => (
                    <SubTaskList
                        key={idx}
                        getPhaseData={item.getData}
                        hidePhaseSummary={hidePhaseSummary}
                        showPhaseSummary={showPhaseSummary}
                        hidePhaseRequestResponseSummary={hidePhaseRequestResponseSummary}
                        showPhaseRequestResponseSummary={showPhaseRequestResponseSummary}
                        deepLinkHash={deepLinkHash}
                        summaryButtonVisible={false}
                        phaseIsSelected={showSummary}
                        onAdding={handleCollapseHtmlEditor}
                        onLoading={handleTaskLoading}
                        onLoaded={handleTaskLoaded}
                        onVisibilityChange={(visible) => handleTaskVisibilityChange(idx, visible)}
                        onCollapsedChange={(collapsed) => handleTaskCollapsedChange(idx, collapsed)}
                        mode={mode}
                        grantData={grantData}
                        nameShownExternally={nameShownExternally}
                        externalPhase={externalPhase}
                    />
                ))}
            </div>

            {/* Nothing is open, so the detail pane has nothing to say and only holds the box open.
                A summary or a subtask still opens into it, even from a collapsed task - the phase
                header's name click and a deep link both arrive that way. */}
            {!hideDetailView && !boxIsEmpty && <div className="task-main-content">
                {!initialLoad && !selectedSummaryPhase && !selectedStep && hasAnyVisibleTasks && (
                    <h5 className="no-task-or-subtask-selected">Select a task or subtask.</h5>
                )}
                {!initialLoad && !selectedSummaryPhase && !selectedStep && !hasAnyVisibleTasks && (
                    <h5 className="no-task-or-subtask-selected">No tasks available for your current permissions.</h5>
                )}

                {showSummary && selectedSummaryPhase && (
                    <TaskSummary
                        phase={selectedSummaryPhase}
                        grantNumber={grantData.grantNumber}
                        grantId={grantData.id}
                        grantData={grantData}
                        hideSummary={false}
                        onLoading={showTaskListPanel}
                        onLoaded={() => taskListloadPanel.current?.instance?.hide()}
                        onClose={() => { hidePhaseRequestResponseSummary() }}
                    />
                )}

                {selectedStep && !showSummary && (
                    <div className="task-detail-body">
                        <div className="task-main-subtask-header">
                            {/* Subtask name (+ due) pinned left, the cycle indicator and close button
                                pinned right. A flex row - not floats - so the height stays stable.
                                The indicator names the cycle this subtask belongs to. */}
                            <div className="task-main-subtask-header-left">
                                <h5 style={{ display: "inline" }}>{getStepName()}</h5>
                                {headerDueDateText() && <span style={{ marginLeft: 8 }}>{headerDueDateText()}</span>}
                            </div>
                            <div className="task-main-subtask-header-right">
                                {mode !== "templates" && (
                                    <TaskCycleIndicator
                                        phase={selectedPhase}
                                        selectedSubmissionId={selectedSubmissionId}
                                    />
                                )}
                                <Button icon="close" stylingMode="text" onClick={onClickClose} />
                            </div>
                        </div>

                        {isCycleClosed && (
                            <div
                                data-testid="closed-cycle-notice"
                                style={{
                                    margin: "8px 0 0", padding: "6px 10px", borderRadius: 4,
                                    backgroundColor: "#fdf3d8", border: "1px solid #e6d9a8", fontSize: 12,
                                }}
                            >
                                {cycleVocabulary.priorMessage}
                            </div>
                        )}

                        <div className="task-main-subtask-form">
                            {isAdhocHybridNew && (
                                <div
                                    data-testid="adhoc-subtask-workflow-picker"
                                    style={{ padding: "8px 16px", backgroundColor: "#f5f5f5", borderRadius: "4px", marginBottom: "12px" }}
                                >
                                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>
                                        Subtask Workflow
                                    </label>
                                    <SelectBox
                                        items={subtaskWorkflowOptions}
                                        displayExpr="name"
                                        valueExpr="id"
                                        value={adhocSubtaskWorkflowType}
                                        onValueChanged={(e) => setAdhocSubtaskWorkflowType(e.value)}
                                        width="100%"
                                        placeholder="Select a workflow..."
                                    />
                                    {adhocSubtaskWorkflowType === null && (
                                        <div style={{ marginTop: "6px", color: "#666", fontSize: "12px" }}>
                                            Pick a workflow type before saving — it can't be changed afterward.
                                        </div>
                                    )}
                                </div>
                            )}
                            <Form formData={selectedStep} labelMode="floating" labelLocation="left" colCount={3} width="100%" onFieldDataChanged={onFieldDataChanged}>
                                {/* Tracked: Name full width */}
                                {selectedPhase?.defaultPhase?.settings?.tracked && mode !== "templates" && (
                                    <GroupItem colSpan={3} colCount={1}>
                                        <Item colSpan={1} dataField="name" cssClass="stepNameTextbox" editorOptions={nameOptions} visible={showName}>
                                            <RequiredRule />
                                        </Item>
                                    </GroupItem>
                                )}

                                {/* Untracked: Name 66%, Completion Date 33% */}
                                {!(selectedPhase?.defaultPhase?.settings?.tracked && mode !== "templates") && (
                                    <GroupItem colSpan={3} colCount={3}>
                                        <Item colSpan={2} dataField="name" cssClass="stepNameTextbox" editorOptions={nameOptions} visible={showName}>
                                            <RequiredRule />
                                        </Item>
                                        {/* Upload-only subtasks are file containers with no due date */}
                                        <Item
                                            colSpan={1}
                                            dataField={dateFieldName}
                                            editorType="dxDateBox"
                                            cssClass="stepDueDateTextbox"
                                            editorOptions={dateOptions}
                                            label={dueDateLabel}
                                            visible={showDueDate}
                                        />
                                    </GroupItem>
                                )}

                                {/* Tracked: All dates on second row - Projected Start (hidden in EndDateOnly mode), Projected Completion, Start Date, Completion Date */}
                                {selectedPhase?.defaultPhase?.settings?.tracked && mode !== "templates" && (
                                    <GroupItem colSpan={3} colCount={selectedPhase?.defaultPhase?.settings?.trackedMode === TrackedMode.EndDateOnly ? 3 : 4} caption="">
                                        {/* Hide Projected Start in EndDateOnly mode - it's auto-calculated from sort order */}
                                        {selectedPhase?.defaultPhase?.settings?.trackedMode !== TrackedMode.EndDateOnly && (
                                            <Item
                                                colSpan={1}
                                                dataField="startDate"
                                                editorType="dxDateBox"
                                                label={PROJECTED_START_LABEL}
                                                editorOptions={projectedStartDateOptions}
                                            />
                                        )}
                                        <Item
                                            colSpan={1}
                                            dataField="projectedEndDate"
                                            editorType="dxDateBox"
                                            label={PROJECTED_COMPLETION_LABEL}
                                            editorOptions={projectedCompletionDateOptions}
                                        />
                                        <Item
                                            colSpan={1}
                                            dataField="submissionDate"
                                            editorType="dxDateBox"
                                            label={START_DATE_LABEL}
                                            editorOptions={startDateOptions}
                                        />
                                        <Item
                                            colSpan={1}
                                            dataField={dateFieldName}
                                            editorType="dxDateBox"
                                            cssClass="stepDueDateTextbox"
                                            editorOptions={dateOptions}
                                            label={dueDateLabel}
                                        />
                                    </GroupItem>
                                )}

                                {/* Upload-only subtasks show their instructions beside the file list instead. */}
                                {workflowType === PhaseWorkflowType.None && !uploadOnly && selectedStep.defaultStep?.instructions && (
                                    <GroupItem caption="" colSpan={3} colCount={3}>
                                        <Item colSpan={2}>
                                            <div style={INSTRUCTIONS_STYLE}>
                                                <span style={{ fontWeight: 600 }}>Instructions: </span><LinkifiedText text={selectedStep.defaultStep.instructions} />
                                            </div>
                                        </Item>
                                        <Item colSpan={1}><></></Item>
                                    </GroupItem>
                                )}

                                {workflowType === PhaseWorkflowType.None && (
                                    <GroupItem caption="" colSpan={3} colCount={submissionSplitLayout ? 2 : 3} cssClass="noneWorkflowFilesGroup">
                                        {/* Upload-only subtasks are file-only: no description. Instructions sit
                                            beside the file list the way the other workflow types put the
                                            description beside it. Split 50/50 rather than the 2/3-1/3 the other types
                                            use - the slot grid needs the room for its Document and Upload columns. */}
                                        {submissionSplitLayout && (
                                            <Item colSpan={1}>
                                                <div style={INSTRUCTIONS_STYLE}>
                                                    <span style={{ fontWeight: 600 }}>Instructions: </span><LinkifiedText text={selectedStep.defaultStep?.instructions} />
                                                </div>
                                            </Item>
                                        )}
                                        {/* Editable and read-only are two items switched with visible,
                                            for the same reason as the name and due date. */}
                                        {!uploadOnly && (
                                            <Item
                                                colSpan={2}
                                                dataField="description"
                                                editorType="dxTextArea"
                                                label={DESCRIPTION_LABEL}
                                                editorOptions={descriptionOptions}
                                                visible={!descriptionReadOnly}
                                            />
                                        )}
                                        {!uploadOnly && (
                                            <Item
                                                colSpan={2}
                                                label={DESCRIPTION_DISPLAY_LABEL}
                                                render={descriptionRender}
                                                visible={descriptionReadOnly}
                                            />
                                        )}
                                        <Item colSpan={uploadOnly && !submissionSplitLayout ? 3 : 1}>
                                            {(mode !== "templates" || selectedStep.id !== "0") && (
                                                <>
                                                    {/* Upload-only subtasks put the picker under the file list and to
                                                        the right, where the slot folders keep their Upload buttons
                                                        (ticket 1034). Every other type keeps it above the list. */}
                                                    {!submissionUploads && (
                                                        <TaskFileUploader
                                                            grant={grantData}
                                                            step={selectedStep}
                                                            xsrfToken={xsrf.value as string}
                                                            isDisabled={uploadDisabled()}
                                                            fileType={mode === "templates" ? CommentType.Request : CommentType.None}
                                                        />
                                                    )}
                                                    <TaskFiles
                                                        grant={grantData}
                                                        step={selectedStep}
                                                        canDelete={!uploadDisabled()}
                                                        canView={true}
                                                        fileType={mode === "templates" ? CommentType.Request : CommentType.None}
                                                        xsrfToken={xsrf.value as string}
                                                    />
                                                    {submissionUploads && (
                                                        <div className="submission-file-uploader">
                                                            <SubmissionFileUploaders
                                                                grant={grantData}
                                                                step={selectedStep}
                                                                xsrfToken={xsrf.value as string}
                                                                isDisabled={uploadDisabled()}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </Item>
                                    </GroupItem>
                                )}

                                <GroupItem caption="" colSpan={3} colCount={3} visible={requestIsVisible()} cssClass="requestGroup">
                                    <Item
                                        colSpan={2}
                                        dataField="requestComment.comment"
                                        editorType="dxHtmlEditor"
                                        label={requestLabel}
                                        editorOptions={requestEditorOptions}
                                        visible={!requestEditorReadOnly}
                                    />
                                    <Item colSpan={2} label={requestLabel} render={requestRender} visible={requestEditorReadOnly} />
                                    <Item colSpan={1}>
                                        {(mode !== "templates" || selectedStep.id !== "0") && (
                                            <>
                                                <TaskFileUploader
                                                    grant={grantData}
                                                    step={selectedStep}
                                                    xsrfToken={xsrf.value as string}
                                                    isDisabled={requestUploadDisabled()}
                                                    fileType={CommentType.Request}
                                                />
                                                <TaskFiles
                                                    grant={grantData}
                                                    step={selectedStep}
                                                    canDelete={!requestUploadDisabled()}
                                                    canView={requestIsVisible()}
                                                    fileType={CommentType.Request}
                                                />
                                            </>
                                        )}
                                    </Item>
                                    <Item colSpan={2} visible={!!requestUpdatedDisplay}>
                                        <div className="comments-last-update">Last update: {requestUpdatedDisplay}</div>
                                    </Item>
                                    <Item colSpan={1} visible={!!requestUpdatedDisplay}>
                                        <></>
                                    </Item>
                                    <Item colSpan={2} visible={mode == "default"}>
                                        <SubTaskRevisionComments grant={grantData} step={selectedStep} isReadOnly={false} />
                                    </Item>
                                    <Item colSpan={1} visible={mode == "default"}>
                                        <></>
                                    </Item>
                                </GroupItem>

                                <GroupItem caption="" colSpan={3} colCount={3} visible={responseIsVisible()} cssClass="responseGroup">
                                    <Item
                                        colSpan={2}
                                        dataField="responseComment.comment"
                                        editorType="dxHtmlEditor"
                                        label={responseLabel}
                                        editorOptions={responseEditorOptions}
                                        visible={!responseEditorReadOnly}
                                    />
                                    <Item colSpan={2} label={responseLabel} render={responseRender} visible={responseEditorReadOnly} />
                                    <Item colSpan={1}>
                                        {(mode !== "templates" || selectedStep.id !== "0") && (
                                            <>
                                                <TaskFileUploader
                                                    grant={grantData}
                                                    step={selectedStep}
                                                    xsrfToken={xsrf.value as string}
                                                    isDisabled={responseUploadDisabled()}
                                                    fileType={CommentType.Response}
                                                />
                                                <TaskFiles
                                                    grant={grantData}
                                                    step={selectedStep}
                                                    canDelete={!responseUploadDisabled()}
                                                    fileType={CommentType.Response}
                                                />
                                            </>
                                        )}
                                    </Item>
                                    <Item colSpan={2} visible={!!selectedStep?.responseComment?.dateUpdated}>
                                        <div className="comments-last-update">Last update: {responseUpdatedDisplay}</div>
                                    </Item>
                                </GroupItem>
                            </Form>
                        </div>

                        <div className="task-main-action-buttons">
                            <div>
                                {canEditAnything && (
                                    <Button
                                        icon="save"
                                        stylingMode="contained"
                                        text="Save Draft"
                                        onClick={() => onClickSave()}
                                        disabled={isAdhocHybridNew && adhocSubtaskWorkflowType === null}
                                    />
                                )}
                            </div>
                            <div>
                                <SubTaskStatusButtons
                                    key={selectedStep?.id ?? 'no-step'}
                                    step={selectedStep}
                                    readyDisabled={readyDisabled}
                                    completeDisabled={completeDisabled}
                                    requireComment={true}
                                    onConfirm={() => onClickSave(true)}
                                    readOnly={flags.readOnly}
                                    workflowType={workflowType}
                                    grant={grantData}
                                    onSave={() => onClickSave()}
                                    onComplete={onClickComplete}
                                    mode={mode}
                                />
                            </div>
                            {commentsAreVisible() && (
                                <div>
                                    <SubTaskComments grantId={grantData.id} grant={grantData} step={selectedStep} />
                                </div>
                            )}
                            <div>
                                {!deleteButtonDisabled() && (
                                    <Button icon="remove" stylingMode="contained" text="Delete" onClick={() => void handleDeleteClick()} />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <LoadPanel
                    delay={300}
                    hideOnOutsideClick={false}
                    ref={taskListloadPanel}
                    shading={true}
                    shadingColor="rgba(0,0,0,0.4)"
                    showIndicator={true}
                    showPane={true}
                    position={{ of: `#${containerId}` }}
                />
            </div>}
        </div>
    );
}
